import React from 'react';
import { Link } from 'react-router-dom';
import { Newspaper } from 'lucide-react';
import newsService, { NewsArticle } from '../../services/newsService';
import ThumbnailImage from '../ThumbnailImage';

const ACCENT = '#3fced1';

/**
 * "Weitere News" – dichtes Karten-Grid (2 Spalten ab sm, 3 ab xl) im Stil der
 * Lead-Sekundär-Karten: Bild oben (aspect-video), Kategorie-/Quellen-Badge,
 * 2-zeiliger Titel, Datum. Hover: Akzent-Border.
 *
 * CLS-Fix (2026-09-01): während die News laden, hält ein Skeleton-Grid mit
 * identischer Kartenstruktur (aspect-video + Textblock) den Platz.
 */
const NewsFeedList: React.FC<{ articles: NewsArticle[]; loading?: boolean }> = ({
  articles,
  loading = false,
}) => {
  if (articles.length === 0) {
    if (!loading) return null;
    return (
      <section aria-label="Weitere News" aria-hidden="true">
        <h2 className="section-heading mb-3 text-base font-bold text-white">Weitere News</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900"
            >
              <div className="aspect-video w-full animate-pulse bg-zinc-800" />
              <div className="p-3">
                <div className="mb-2 h-[19px] w-24 animate-pulse rounded bg-zinc-800" />
                <div className="h-3.5 w-4/5 animate-pulse rounded bg-zinc-800" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Weitere News">
      <h2 className="section-heading mb-3 text-base font-bold text-white">Weitere News</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {articles.map((item) => (
          <NewsCard key={item._id || item.id || item.slug} article={item} />
        ))}
      </div>
    </section>
  );
};

export const NewsCard: React.FC<{ article: NewsArticle }> = ({ article }) => {
  const image = newsService.getImageUrl(article);
  const badge = article.category || newsService.getDisplaySource(article);

  return (
    <Link
      to={article.slug ? `/news/${article.slug}` : '#'}
      className="group flex flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition-colors hover:border-[#3fced1]/50"
    >
      <div className="relative aspect-video w-full overflow-hidden bg-zinc-800">
        {image ? (
          // Karten-Breite max. ~370px → 400er-Thumbnail statt Original-Poster
          <ThumbnailImage
            src={image}
            thumbSize={400}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Newspaper size={24} className="text-zinc-600" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-3">
        {/* Kategorie-/Quellen-Chip + Zeit über dem Titel (links ausgerichtet) */}
        <div className="mb-2 flex items-center gap-1.5">
          {badge && (
            <span
              className="max-w-[70%] shrink-0 truncate rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-black"
              style={{ backgroundColor: ACCENT }}
            >
              {badge}
            </span>
          )}
          <span className="truncate text-[10.5px] text-zinc-500">
            {newsService.formatArticleTime(article)}
          </span>
        </div>
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {newsService.formatTitle(article)}
        </h3>
      </div>
    </Link>
  );
};

export default NewsFeedList;
